<script setup lang="ts">
import { SwiperSlide } from 'swiper/vue';
import { computed, ref } from 'vue';

import { useGlobalStyle } from '../../store/templateData';
import ModuleCarouselSwiper from '../ModuleCarouselSwiper/ModuleCarouselSwiper.vue';
import IMAGE_DEFAULT_BACKGROUND from './assets/default-background.png';
import Cover from './components/Cover/Cover.vue';
import Hide from './components/Hide/Hide.vue';
import InputMessage from './components/InputMessage/InputMessage.vue';
import Interaction from './components/Interaction/Interaction.vue';
import NickName from './components/NickName/NickName.vue';
import SendMessage from './components/SendMessage/SendMessage.vue';

defineOptions({
  name: 'ModuleCarouselInteractionInstructionDisplay'
});

interface Props {
  module: {
    components: {
      title: {
        text?: string;
      };
      cover: {
        backgroundColor?: string;
      };
      stream: {
        backgroundSrc?: string;
        productSrc?: string;
      };
    };
  };
}

const props = withDefaults(defineProps<Props>(), {
  module: () => ({
    components: {
      title: { text: '' },
      cover: { backgroundColor: '' },
      stream: { backgroundSrc: '', productSrc: '' }
    }
  })
});

const components = computed(() => props.module.components);
const streamBackgroundSrc = computed(() => {
  return components.value.stream.backgroundSrc
    ? components.value.stream.backgroundSrc
    : IMAGE_DEFAULT_BACKGROUND;
});

const activeSlide = ref(0);
const handleSlideChange = (index: number) => {
  activeSlide.value = index;
};

const { titleStyle } = useGlobalStyle();
</script>

<template>
<div class="module-carousel-interaction-instruction-display">
  <div
    class="module-carousel-interaction-instruction-display__title"
    v-if="components.title.text"
    :style="titleStyle"
  >
    {{ components.title.text }}
  </div>
  <ModuleCarouselSwiper @slideChange="handleSlideChange">
    <template #default="{ swiperRef }">
      <SwiperSlide :swiperRef="swiperRef">
        <Cover :coverColor="components.cover.backgroundColor ?? ''"/>
      </SwiperSlide>
      <SwiperSlide :swiperRef="swiperRef">
        <InputMessage
          :backgroundImage="streamBackgroundSrc"
          :productImage="components.stream.productSrc"
          :isMsgShow="activeSlide === 1"
        />
      </SwiperSlide>
      <SwiperSlide :swiperRef="swiperRef">
        <NickName
          :backgroundImage="streamBackgroundSrc"
          :productImage="components.stream.productSrc"
          :isMsgShow="activeSlide === 2"
        />
      </SwiperSlide>
      <SwiperSlide :swiperRef="swiperRef">
        <SendMessage
          :backgroundImage="streamBackgroundSrc"
          :productImage="components.stream.productSrc"
          :isMsgShow="activeSlide === 3"
        />
      </SwiperSlide>
      <SwiperSlide :swiperRef="swiperRef">
        <Interaction
          :backgroundImage="streamBackgroundSrc"
          :productImage="components.stream.productSrc"
          :isMsgShow="activeSlide === 4"
        />
      </SwiperSlide>
      <SwiperSlide :swiperRef="swiperRef">
        <Hide
          :backgroundImage="streamBackgroundSrc"
          :productImage="components.stream.productSrc"
          :isMsgShow="activeSlide === 5"
        />
      </SwiperSlide>
    </template>
  </ModuleCarouselSwiper>
</div>
</template>
<style lang="scss" scoped>
$desktop-fixed-width: 1075px;

.module-carousel-interaction-instruction-display {
  @apply py-4;
  @apply mx-auto;

  &__title {
    @apply mb-4 font-medium break-all;
    @apply text-4xl text-center whitespace-pre-line;
    // mobile版圖片滿版，但文字要與其他 module 等寬 (留 20px 的 padding)
    @apply px-5;

    word-wrap: break-word;
  }
}

// tablet
.tablet .module-carousel-interaction-instruction-display {
  @apply px-10;

  &__title {
    @apply px-0;
  }
}

// PC - 1024px
.laptop .module-carousel-interaction-instruction-display {
  padding-left: 10%;
  padding-right: 10%;

  @apply py-8;

  &__title {
    @apply px-0;
  }
}

// PC -1440px
.desktop .module-carousel-interaction-instruction-display {
  width: $desktop-fixed-width;

  @apply py-8;

  &__title {
    @apply px-0;
  }
}
</style>
